import React, { createContext, useState } from 'react';
import { API, defaultError, config, closeSession } from "../config";
import axios from "axios";
import useSWR from 'swr';

const MyProceduresContext = createContext();

const MyProceduresProvider = ({ children }) => {
    const [error, setError] = useState({ "error": false, "message": "" });
    const [procedure, setProcedure] = useState({ "procedureTitle": "", "procedureDescription": "" });
    const [procedures, setProcedures] = useState([]);
    const [sorters, setSorters] = useState({ "sorter": "name", "order": "asc" });
    const [page, setPage] = useState(0);
    const [pageAdmin, setPageAdmin] = useState(0);
    const [maxPage, setMaxPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [procedureTypeId, setProcedureTypeId] = useState(1);
    const [procedureClassId, setProcedureClassId] = useState("T1");

    const fetcher = () => axios.get(`${API}/procedures/getallprocedures/${procedureTypeId}/${procedureClassId}/${sorters.sorter}/${sorters.order}/${page}`).then(res => {
        setMaxPage(res.data.maxPage);
        return res.data.response
    });

    function useProcedures() {
        const { data, error } = useSWR(`${API}/procedures/getallprocedures/${procedureTypeId}/${procedureClassId}/${sorters.sorter}/${sorters.order}/${page}`, fetcher);
        return {
            procedures: data,
            isLoading: !error && !data,
            isError: error
        }
    }

    const getProceduresAdmin = async (procedureTypeId) => {
        setError({ "error": false, "message": "" });
        setLoading(true);
        await axios.get(`${API}/procedures/procedures/${procedureTypeId}/${pageAdmin}`, config).then(res => {
            if (res.data.error) {
                if (res.data.status == "400-1") {
                    closeSession();
                }
                setLoading(false);
                //seterror and put false after 3 seconds
                setError({ "error": true, "message": res.data.response });
                return;
            }
            setMaxPage(res.data.maxPage);
            setProcedures(res.data.response);
        }).catch(err => {
            setError(defaultError);
        })
        setLoading(false);
    }

    const getFile = async (filePath, procedureTitle) => {
        setLoading(true);
        await axios.get(`${API}/procedures/getfile/${filePath}`).then(res => {
            setError({ "error": false, "message": "" });
            if (res.data.error) {
                setLoading(false);
                //seterror and put false after 3 seconds
                setError({ "error": true, "message": res.data.response });
                setTimeout(() => {
                    setError({ "error": false, "message": "" });
                }, 3000);
                
                return;
            }
            
            const binaryString = window.atob(res.data.response);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            const url = window.URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));

            const link = document.createElement('a');
            link.href = url;
            //link.setAttribute('download', filePath);
            link.setAttribute('download', procedureTitle + " " + filePath.substring(filePath.length - 14) /* procedureTitle.replace(".", "") */);
            document.body.appendChild(link);
            link.click();
            setLoading(false);
        }).catch(err => {
            setLoading(false);
            setError({ "error": true, "message": "Ha ocurrido un error al descargar el archivo" });
            setTimeout(() => {
                setError({ "error": false, "message": "" });
            }, 3000);
        });
    }

    const addProcedure = async (data, procedureTypeId) => {
        setLoading(true);
        var formData = new FormData();
        formData.append("procedureFile", data.procedureFile.length > 0 ? data.procedureFile[0] : new File([], "empty"));
        formData.append("procedureTitle", data.procedureTitle);
        formData.append("procedureDescription", data.procedureDescription);
        formData.append("procedureClassId", data.procedureClassId);

        config.headers["Content-Type"] = "multipart/form-data";
        try {
            const res = await axios.post(`${API}/procedures/addprocedure/${procedureTypeId}`, formData, config)
            if (res.data.status == "400-1") {
                closeSession();
            }
            if (!res.data.error) {
                getProceduresAdmin(procedureTypeId);
            }
            setLoading(false);
            return res.data;
        } catch (err) {
            setLoading(false);
            return defaultError;
        }
    }

    const updateProcedure = async (data, procedureId, procedureTypeId) => {
        setLoading(true);
        var formData = new FormData();
        formData.append("procedureFile", data.procedureFile.length > 0 ? data.procedureFile[0] : new File([], "empty"));
        formData.append("procedureTitle", data.procedureTitle);
        formData.append("procedureDescription", data.procedureDescription);
        formData.append("procedureClassId", data.procedureClassId);

        config.headers["Content-Type"] = "multipart/form-data";
        try {
            const res = await axios.put(`${API}/procedures/updateprocedure/${procedureId}`, formData, config)
            if (res.data.status == "400-1") {
                closeSession();
            }
            if (!res.data.error) {
                getProceduresAdmin(procedureTypeId);
            }
            setLoading(false);
            return res.data;
        } catch (err) {
            setLoading(false);
            return defaultError;
        }
    }

    const changeProcedureVisibility = async (procedureId, procedureTypeId) => {
        try {
            const res = await axios.put(`${API}/procedures/changeprocedurevisibility/${procedureId}`, {}, config)
            if (res.data.status == "400-1") {
                closeSession();
            }
            if (!res.data.error) {
                getProceduresAdmin(procedureTypeId);
            }
            return res.data;
        } catch (err) {
            return defaultError;
        }
    }

    const deleteProcedure = async (procedureId, procedureTypeId) => {
        setLoading(true);
        try {
            const res = await axios.delete(`${API}/procedures/deleteprocedure/${procedureId}`, config)
            if (res.data.status == "400-1") {
                closeSession();
            }
            if (!res.data.error) {
                getProceduresAdmin(procedureTypeId);
            }
            setLoading(false);
            return res.data;
        } catch (err) {
            setLoading(false);
            return defaultError;
        }
    }

    return (
        <MyProceduresContext.Provider value={{
            error,
            setError,
            procedure,
            setProcedure,
            sorters,
            setSorters,
            maxPage,
            setPage,
            useProcedures,
            getFile,
            addProcedure,
            updateProcedure,
            changeProcedureVisibility,
            deleteProcedure,
            procedures,
            getProceduresAdmin,
            loading,
            setLoading,
            pageAdmin,
            setPageAdmin,
            procedureTypeId,
            setProcedureTypeId,
            procedureClassId,
            setProcedureClassId
        }}>
            {children}
        </MyProceduresContext.Provider>
    );
}

export { MyProceduresProvider };
export default MyProceduresContext;